import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { Card, CardBody, Container, Table } from "reactstrap";
import { toast } from "react-toastify";

import Loaders from "components/Common/Loaders";
import MetadataFilterToolbar from "components/Common/MetadataFilterToolbar";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import AIFadvClientsModal from "pages/AllCommonModal/AIFadvClientsModal/ai-fadv-clients-modal";
import { mapApiMetadataToTableColumnDefinition } from "helpers/GeneralHelper";
import {
  countActiveMetadataFilters,
  getAsyncFilterLabelFieldKey,
  isAsyncFilterLabelFieldKey,
  isMetadataAsyncSelectFilterDefinition,
  mapFilterDefinitionsToModalData,
  mergeModalSubmitIntoListFilter,
  normalizeMetadataFilterDateForApi,
} from "helpers/metadataFilterHelpers";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import {
  fetchAIFadvReportMetadata,
  submitAIFadvReportFilterData,
} from "model/AIFadvReportModel";

const COUNT_COLUMN_STATUS_BY_ACCESSOR = {
  activeClientCount: { statusId: "active", statusLabel: "Active" },
  deactiveClientCount: { statusId: "deactive", statusLabel: "Deactive" },
};

const strOrNull = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed === "" ? null : trimmed;
};

const extractFilterResponseRows = (response = {}) => {
  const responseData = response?.data ?? response ?? {};
  const nestedData =
    responseData?.data &&
    typeof responseData.data === "object" &&
    !Array.isArray(responseData.data)
      ? responseData.data
      : responseData;

  if (Array.isArray(nestedData?.rows)) return nestedData.rows;
  if (Array.isArray(responseData?.rows)) return responseData.rows;
  if (Array.isArray(nestedData?.records)) return nestedData.records;
  if (Array.isArray(responseData?.records)) return responseData.records;
  if (Array.isArray(nestedData?.data)) return nestedData.data;
  if (Array.isArray(responseData?.data)) return responseData.data;
  if (Array.isArray(nestedData)) return nestedData;
  if (Array.isArray(responseData)) return responseData;
  return [];
};

const enhanceMetadataColumns = (columns = []) =>
  (Array.isArray(columns) ? columns : []).map((col = {}) => {
    const accessor = String(col?.accessor ?? "").trim();
    const countMeta = COUNT_COLUMN_STATUS_BY_ACCESSOR[accessor];
    if (!countMeta) return col;
    return {
      ...col,
      isClientCount: true,
      statusId: countMeta.statusId,
      statusLabel: countMeta.statusLabel,
    };
  });

const mapRowsFromApi = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map((item = {}, index) => ({
    ...item,
    id: item?.id ?? item?._id ?? `ai-fadv-row-${index}`,
    aiFadvUser: item?.aiFadvUser ?? "",
    activeClientCount: Number(item?.activeClientCount) || 0,
    deactiveClientCount: Number(item?.deactiveClientCount) || 0,
  }));

const normalizeAIFadvFilterPayload = (raw = {}, metadataFilters = []) => {
  const outbound = { ...raw };

  (Array.isArray(metadataFilters) ? metadataFilters : []).forEach((filterDef) => {
    const fieldKey = filterDef?.field;
    if (!fieldKey || !(fieldKey in outbound)) return;
    if (filterDef?.type === "date") {
      if (!outbound[fieldKey] && outbound[fieldKey] !== 0) outbound[fieldKey] = null;
      else if (outbound[fieldKey] === "") outbound[fieldKey] = null;
    } else {
      outbound[fieldKey] = strOrNull(outbound[fieldKey]);
    }
  });

  Object.keys(outbound).forEach((key) => {
    if (isAsyncFilterLabelFieldKey(key)) delete outbound[key];
  });

  delete outbound.pageNumber;
  delete outbound.pageSize;

  return outbound;
};

const buildBaselineEmptyFilterState = (metadataFilters = []) => {
  const baseline = {};
  (Array.isArray(metadataFilters) ? metadataFilters : []).forEach((filterDef) => {
    const fieldKey = filterDef?.field;
    if (!fieldKey) return;
    baseline[fieldKey] = filterDef?.type === "date" ? null : "";
    if (isMetadataAsyncSelectFilterDefinition(filterDef)) {
      baseline[getAsyncFilterLabelFieldKey(fieldKey)] = "";
    }
  });
  return baseline;
};

const AIFadvReport = () => {
  const { fetchMetadataSchema, getDropdownOptionsByApiKey } = useCommonFilterOptions();

  const [listFilter, setListFilter] = useState({});
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMetaData, setTableMetaData] = useState([]);
  const [filterMetaData, setFilterMetaData] = useState([]);
  const [toolbarDropdownOptionsByFieldName, setToolbarDropdownOptionsByFieldName] =
    useState({});
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [commonResetFilter, setCommonResetFilter] = useState(false);
  const [toolbarFilterRemountKey, setToolbarFilterRemountKey] = useState(0);
  const [clientsModalOpen, setClientsModalOpen] = useState(false);
  const [clientsModalContext, setClientsModalContext] = useState(null);

  const toolbarFilterDefinitions = useMemo(() => {
    const allFilters = Array.isArray(filterMetaData) ? filterMetaData : [];
    const defaults = allFilters.filter(
      (filterDef) => filterDef?.field && filterDef?.isDefault === true
    );
    return defaults.length
      ? defaults
      : allFilters.filter((filterDef) => filterDef?.field);
  }, [filterMetaData]);

  const modalOnlyFilterDefinitions = useMemo(() => {
    const allFilters = Array.isArray(filterMetaData) ? filterMetaData : [];
    const hasDefaultFlags = allFilters.some(
      (filterDef) => filterDef?.isDefault === true
    );
    if (!hasDefaultFlags) return [];
    return allFilters.filter(
      (filterDef) => filterDef?.field && filterDef?.isDefault !== true
    );
  }, [filterMetaData]);

  const shouldShowTableHeaderFilterAndResetControls =
    modalOnlyFilterDefinitions.length > 0;

  const activeModalFilterCount = useMemo(
    () => countActiveMetadataFilters(listFilter, modalOnlyFilterDefinitions),
    [listFilter, modalOnlyFilterDefinitions]
  );

  const filterModalData = useMemo(
    () => mapFilterDefinitionsToModalData(modalOnlyFilterDefinitions, listFilter),
    [modalOnlyFilterDefinitions, listFilter]
  );

  const buildPayloadFromFilterForm = useCallback(
    (form = {}, overrides = {}, metadataOverride = null) => {
      const metadataFilters = metadataOverride || filterMetaData;
      const metadataValues = {};

      (Array.isArray(metadataFilters) ? metadataFilters : []).forEach((filterDef) => {
        const fieldKey = filterDef?.field;
        if (!fieldKey) return;
        let value = form?.[fieldKey];
        if (filterDef?.type === "date") {
          value = normalizeMetadataFilterDateForApi(value);
        }
        metadataValues[fieldKey] = value;
      });

      return normalizeAIFadvFilterPayload(
        {
          ...metadataValues,
          ...overrides,
        },
        metadataFilters
      );
    },
    [filterMetaData]
  );

  const getAIFadvReportList = async (
    payloadOverrides = {},
    metadataOverride = null
  ) => {
    const metadataFilters = metadataOverride || filterMetaData;
    setLoading(true);
    try {
      const payload = normalizeAIFadvFilterPayload(
        payloadOverrides,
        metadataFilters
      );

      const response = await submitAIFadvReportFilterData(payload);
      const rows = extractFilterResponseRows(response);

      if (response == null || response?.success === false) {
        setReportData([]);
        if (response?.message) {
          toast.error(response.message);
        }
        return;
      }

      setReportData(mapRowsFromApi(rows));
    } catch (error) {
      toast.error(error?.message || "Unable to load AI FADV report");
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const toolbarDropdownDefinitions = toolbarFilterDefinitions.filter(
      (filterDef) => filterDef?.type === "dropdown" && filterDef?.field
    );
    if (!toolbarDropdownDefinitions.length) {
      setToolbarDropdownOptionsByFieldName({});
      return;
    }

    let requestCancelled = false;
    (async () => {
      const optionListTuples = await Promise.all(
        toolbarDropdownDefinitions.map(async (filterDef) => {
          const fieldKey = filterDef.field;
          const staticOptions = Array.isArray(filterDef?.itemArray)
            ? filterDef.itemArray
            : [];
          if (staticOptions.length) {
            return [fieldKey, staticOptions];
          }
          const remoteOptions = await getDropdownOptionsByApiKey(filterDef?.api);
          return [fieldKey, remoteOptions];
        })
      );
      if (requestCancelled) return;
      const nextOptions = {};
      optionListTuples.forEach(([fieldKey, options]) => {
        nextOptions[fieldKey] = options;
      });
      setToolbarDropdownOptionsByFieldName(nextOptions);
    })();

    return () => {
      requestCancelled = true;
    };
  }, [toolbarFilterDefinitions, getDropdownOptionsByApiKey]);

  useEffect(() => {
    if (!Array.isArray(filterMetaData) || !filterMetaData.length) return;
    setListFilter((previous) => {
      const merged = { ...previous };
      let changed = false;
      filterMetaData.forEach((filterDef) => {
        const fieldKey = filterDef?.field;
        if (!fieldKey) return;
        if (!(fieldKey in merged)) {
          merged[fieldKey] = filterDef?.type === "date" ? null : "";
          changed = true;
        }
        if (isMetadataAsyncSelectFilterDefinition(filterDef)) {
          const labelKey = getAsyncFilterLabelFieldKey(fieldKey);
          if (!(labelKey in merged)) {
            merged[labelKey] = "";
            changed = true;
          }
        }
      });
      return changed ? merged : previous;
    });
  }, [filterMetaData]);

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      try {
        const metaRes = await fetchMetadataSchema("metadata:aiFadvUserWiseClient", () =>
          fetchAIFadvReportMetadata()
        );
        const meta = metaRes?.data ?? metaRes ?? {};
        const metadataFilters = Array.isArray(meta?.filters) ? meta.filters : [];
        const tableColumns = meta?.table?.columns?.length
          ? enhanceMetadataColumns(
              meta.table.columns.map((col) => mapApiMetadataToTableColumnDefinition(col))
            )
          : enhanceMetadataColumns([
              { Header: "AI FADV User", accessor: "aiFadvUser" },
              { Header: "Active Client", accessor: "activeClientCount" },
              { Header: "Deactive Client", accessor: "deactiveClientCount" },
            ]);

        setTableMetaData(tableColumns);
        setFilterMetaData(metadataFilters);

        const initialFilterState = buildBaselineEmptyFilterState(metadataFilters);
        setListFilter(initialFilterState);

        await getAIFadvReportList(
          buildPayloadFromFilterForm(initialFilterState, {}, metadataFilters),
          metadataFilters
        );
      } catch (error) {
        toast.error(error?.message || "Unable to load AI FADV report metadata");
        setTableMetaData(
          enhanceMetadataColumns([
            { Header: "AI FADV User", accessor: "aiFadvUser" },
            { Header: "Active Client", accessor: "activeClientCount" },
            { Header: "Deactive Client", accessor: "deactiveClientCount" },
          ])
        );
        setFilterMetaData([]);
        setLoading(false);
      }
    };
    bootstrap();
  }, []);

  useEffect(() => {
    if (!commonResetFilter) return;
    const run = async () => {
      await handleFilterReset();
    };
    run();
  }, [commonResetFilter]);

  const handleToolbarTextChange = (fieldKey, nextValue) => {
    setListFilter((previous) => ({
      ...previous,
      [fieldKey]: nextValue ?? "",
    }));
  };

  const handleToolbarSelectChange = (fieldKey, nextOption) => {
    setListFilter((previous) => ({
      ...previous,
      [fieldKey]: nextOption?.value ?? "",
      [getAsyncFilterLabelFieldKey(fieldKey)]: nextOption?.label ?? "",
    }));
  };

  const handleUpperToolbarSearch = async () => {
    await getAIFadvReportList(buildPayloadFromFilterForm(listFilter));
  };

  const handleFilterReset = async () => {
    const emptyFilter = buildBaselineEmptyFilterState(filterMetaData);
    setListFilter(emptyFilter);
    setToolbarFilterRemountKey((previous) => previous + 1);
    setCommonResetFilter(false);
    await getAIFadvReportList(buildPayloadFromFilterForm(emptyFilter));
  };

  const handleModalFilterSubmit = async (filterFormValuesFromModal = {}) => {
    const mergedFilter = mergeModalSubmitIntoListFilter(
      listFilter,
      filterFormValuesFromModal,
      modalOnlyFilterDefinitions
    );
    setListFilter(mergedFilter);
    await getAIFadvReportList(buildPayloadFromFilterForm(mergedFilter));
  };

  const handleClientCountClick = (row = {}, column = {}) => {
    const statusId = String(column?.statusId ?? "").trim();
    if (!statusId) {
      toast.error("Unable to open client list for this count.");
      return;
    }

    setClientsModalContext({
      aiFadvUser: String(row?.aiFadvUser ?? ""),
      statusId,
      statusLabel: column?.statusLabel || column?.Header || statusId,
    });
    setClientsModalOpen(true);
  };

  const closeClientsModal = () => {
    setClientsModalOpen(false);
    setClientsModalContext(null);
  };

  const renderCellValue = (column = {}, row = {}) => {
    const accessor = column?.accessor;
    const value = row?.[accessor];

    if (column?.isClientCount) {
      const displayValue =
        value === null || value === undefined || value === "" ? 0 : value;
      const count = Number(displayValue) || 0;
      if (count <= 0) {
        return <span className="text-muted">0</span>;
      }
      return (
        <button
          type="button"
          className="status-columns-report-value"
          onClick={() => handleClientCountClick(row, column)}
        >
          {displayValue}
        </button>
      );
    }

    if (value === null || value === undefined || String(value).trim() === "") {
      return <span className="text-muted">—</span>;
    }

    return <span>{value}</span>;
  };

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const handleSort = (accessor) => {
    if (!accessor) return;
    setSortConfig((prev) => {
      if (prev.key === accessor) {
        return {
          key: accessor,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key: accessor, direction: "asc" };
    });
  };

  const filteredData = useMemo(() => {
    const rows = Array.isArray(reportData) ? reportData : [];
    if (!sortConfig.key) return rows;

    return [...rows].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      return sortConfig.direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [reportData, sortConfig]);

  const SortIcon = ({ column }) => {
    const isActive = sortConfig.key === column;
    const direction = isActive ? sortConfig.direction : null;
    const activeColor = "#212529";
    const inactiveColor = "#c4c4c4";

    return (
      <span
        style={{
          display: "inline-flex",
          flexDirection: "column",
          lineHeight: 0,
          marginLeft: 4,
          verticalAlign: "middle",
        }}
      >
        <svg width="8" height="5" viewBox="0 0 8 5" style={{ display: "block" }}>
          <path d="M4 0l4 5H0z" fill={direction === "asc" ? activeColor : inactiveColor} />
        </svg>
        <svg width="8" height="5" viewBox="0 0 8 5" style={{ display: "block", marginTop: 2 }}>
          <path d="M4 5L0 0h8z" fill={direction === "desc" ? activeColor : inactiveColor} />
        </svg>
      </span>
    );
  };

  return (
    <React.Fragment>
      <div className="page-content ai-fadv-report-page">
        <Container fluid>
          <Card>
            <CardBody>
              <h5 className="fw-bold mb-3">AI FADV Report</h5>
              <hr />

              {toolbarFilterDefinitions.length ? (
                <MetadataFilterToolbar
                  remountKey={toolbarFilterRemountKey}
                  toolbarDefinitions={toolbarFilterDefinitions}
                  listFilter={listFilter}
                  onTextChange={handleToolbarTextChange}
                  onSelectChange={handleToolbarSelectChange}
                  onSearch={handleUpperToolbarSearch}
                  onClear={handleFilterReset}
                  showClearWhenNoModalFilters={!shouldShowTableHeaderFilterAndResetControls}
                  toolbarDropdownOptionsByFieldName={toolbarDropdownOptionsByFieldName}
                  pageName="AIFadvReport"
                />
              ) : null}

              {shouldShowTableHeaderFilterAndResetControls ? (
                <div className="d-flex justify-content-end gap-2 mb-2">
                  <button
                    type="button"
                    className="btn btn-link p-0"
                    onClick={() => setCommonFilterModalOpen(true)}
                  >
                    FILTERS{activeModalFilterCount ? ` (${activeModalFilterCount})` : ""}
                  </button>
                  <button
                    type="button"
                    className="btn btn-link p-0"
                    onClick={() => setCommonResetFilter(true)}
                  >
                    Reset
                  </button>
                </div>
              ) : null}

              {loading ? (
                <Loaders />
              ) : !tableMetaData?.length ? (
                <div className="text-muted text-center py-5">
                  Unable to load table columns.
                </div>
              ) : (
                <div className="table-responsive">
                  <Table
                    className="align-middle table-nowrap mb-0 ai-fadv-report-table"
                    hover
                  >
                    <thead className="table-light">
                      <tr>
                        {tableMetaData.map((column) => (
                          <th
                            key={column.accessor || column.Header}
                            onClick={() => handleSort(column?.accessor)}
                            style={{ cursor: "pointer" }}
                          >
                            {column.Header} <SortIcon column={column?.accessor} />
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.length ? (
                        filteredData.map((row, rowIndex) => (
                          <tr key={row?.id || `ai-fadv-row-${rowIndex}`}>
                            {tableMetaData.map((column) => (
                              <td key={column.accessor || column.Header}>
                                {renderCellValue(column, row)}
                              </td>
                            ))}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={tableMetaData.length || 1}
                            className="text-center text-muted py-4"
                          >
                            No records found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      {shouldShowTableHeaderFilterAndResetControls ? (
        <AllCommonFilterModal
          setCommonFilterModalOpen={setCommonFilterModalOpen}
          commonFilterModalOpen={commonFilterModalOpen}
          modalTitle="AI FADV Report Filter"
          handleSubmit={handleModalFilterSubmit}
          filterData={filterModalData}
          commonResetFilter={commonResetFilter}
          setCommonResetFilter={setCommonResetFilter}
        />
      ) : null}

      <AIFadvClientsModal
        isOpen={clientsModalOpen}
        onClose={closeClientsModal}
        aiFadvUser={clientsModalContext?.aiFadvUser}
        statusId={clientsModalContext?.statusId}
        statusLabel={clientsModalContext?.statusLabel}
      />
    </React.Fragment>
  );
};

AIFadvReport.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(AIFadvReport);
